import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ShieldCheck, ShieldAlert, Shield, Clock, AlertTriangle, ArrowRight, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Verification status card for the Agency Dashboard — mirrors the Profile
 * Completion card. Shows a percent-style progress (docs uploaded / required)
 * plus a status pill, and the whole card is a Link to /verification so a
 * single click takes the user into the verification flow.
 *
 * Statuses (backend `/api/verification/status`):
 *   not_started → 0% · "Get verified" CTA
 *   documents_uploaded → ~33% · "Submit for review"
 *   under_review → ~66% · "We're reviewing"
 *   verified → 100% · "Verified" badge
 *   rejected → red · "Action required"
 *   expired → amber · "Renew"
 */
export default function VerificationStatusCard() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const tt = (en, es) => (language === 'es' ? es : en);

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/verification/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ status: 'not_started', required_docs: [], uploaded_docs: [] }))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <Card data-testid="verification-status-card-loading">
        <CardContent className="p-4 flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          {tt('Loading verification...', 'Cargando verificación...')}
        </CardContent>
      </Card>
    );
  }

  if (!status) return null;

  const required = (status.required_docs || []).length;
  const uploaded = (status.uploaded_docs || []).length;
  const docsPct = required > 0 ? Math.min(100, Math.round((uploaded / required) * 100)) : 0;

  // Map status → display config
  const CONFIG = {
    not_started: {
      pct: 0,
      pillClass: 'bg-slate-100 text-slate-600 border-slate-200',
      barClass: 'bg-slate-300',
      icon: Shield,
      iconClass: 'text-slate-500',
      label: tt('Not started', 'No iniciado'),
      hint: tt('Get verified to unlock the gold badge.', 'Verifícate para obtener la insignia dorada.'),
      cta: tt('Start verification →', 'Iniciar verificación →'),
    },
    documents_uploaded: {
      // 10% baseline + up to 40% based on docs ratio → caps at 50% before admin review
      pct: Math.min(50, 10 + Math.round(docsPct * 0.4)),
      pillClass: 'bg-blue-50 text-blue-700 border-blue-200',
      barClass: 'bg-blue-400',
      icon: Clock,
      iconClass: 'text-blue-600',
      label: tt('Docs uploaded', 'Documentos subidos'),
      hint: tt('Submit your documents for AI review.', 'Envía tus documentos para revisión.'),
      cta: tt('Continue →', 'Continuar →'),
    },
    // Backend also emits "pending" as a shorthand for awaiting review after upload.
    pending: {
      pct: 50,
      pillClass: 'bg-blue-50 text-blue-700 border-blue-200',
      barClass: 'bg-blue-400',
      icon: Clock,
      iconClass: 'text-blue-600',
      label: tt('Pending review', 'Pendiente de revisión'),
      hint: tt("Your documents are queued for review. We'll email you when it's done.", 'Tus documentos están en cola para revisión.'),
      cta: tt('View status →', 'Ver estado →'),
    },
    under_review: {
      pct: 66,
      pillClass: 'bg-amber-50 text-amber-700 border-amber-200',
      barClass: 'bg-amber-400',
      icon: Clock,
      iconClass: 'text-amber-600',
      label: tt('Under review', 'En revisión'),
      hint: tt("We're reviewing your documents. You'll get an email shortly.", 'Estamos revisando tus documentos.'),
      cta: tt('View status →', 'Ver estado →'),
    },
    ai_reviewed: {
      pct: 80,
      pillClass: 'bg-amber-50 text-amber-700 border-amber-200',
      barClass: 'bg-amber-400',
      icon: Clock,
      iconClass: 'text-amber-600',
      label: tt('AI reviewed', 'IA revisó'),
      hint: tt('Awaiting final admin approval.', 'Esperando aprobación final.'),
      cta: tt('View status →', 'Ver estado →'),
    },
    verified: {
      pct: 100,
      pillClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      barClass: 'bg-emerald-500',
      icon: ShieldCheck,
      iconClass: 'text-emerald-600',
      label: tt('Verified', 'Verificado'),
      hint: tt("You're fully verified. The gold badge is live on every listing.", 'Estás verificado. La insignia dorada aparece en tus listados.'),
      cta: tt('View certificate →', 'Ver certificado →'),
    },
    rejected: {
      pct: 100,
      pillClass: 'bg-red-50 text-red-700 border-red-200',
      barClass: 'bg-red-400',
      icon: XCircle,
      iconClass: 'text-red-600',
      label: tt('Rejected', 'Rechazado'),
      hint: status.admin_notes
        ? `${tt('Reason', 'Motivo')}: ${status.admin_notes}`
        : tt('Please re-submit corrected documents.', 'Vuelve a enviar los documentos corregidos.'),
      cta: tt('Re-submit →', 'Reenviar →'),
    },
    expired: {
      pct: 100,
      pillClass: 'bg-amber-50 text-amber-700 border-amber-200',
      barClass: 'bg-amber-400',
      icon: AlertTriangle,
      iconClass: 'text-amber-600',
      label: tt('Expired', 'Expirado'),
      hint: tt('Your verification has expired. Renew to keep the gold badge.', 'Tu verificación expiró. Renueva para mantener la insignia.'),
      cta: tt('Renew →', 'Renovar →'),
    },
  };
  // Defensive fallback: if backend emits an unknown status, log it and assume
  // the verification is in-flight (closer to truth than "Not started").
  let cfg = CONFIG[status.status];
  if (!cfg) {
    if (typeof console !== 'undefined') console.warn(`[VerificationStatusCard] unknown status: ${status.status}`);
    cfg = uploaded > 0 ? CONFIG.pending : CONFIG.not_started;
  }
  const Icon = cfg.icon;

  return (
    <Link
      to="/verification"
      className="block group"
      data-testid="verification-status-card-link"
    >
      <Card
        className="transition-all hover:border-[#D4AF37] hover:shadow-md cursor-pointer"
        data-testid="verification-status-card"
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Icon className={`w-4 h-4 ${cfg.iconClass}`} />
              <p className="font-semibold text-sm">{tt('Verification', 'Verificación')}</p>
            </div>
            <span
              className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${cfg.pillClass}`}
              data-testid="verification-status-pill"
            >
              {cfg.label}
            </span>
          </div>
          <div className="flex items-end gap-1 mb-2">
            <span className="font-heading text-2xl font-bold text-slate-900" data-testid="verification-status-pct">
              {cfg.pct}%
            </span>
            {required > 0 && status.status !== 'verified' && (
              <span className="text-xs text-slate-400 mb-1">
                · {uploaded}/{required} {tt('docs', 'docs')}
              </span>
            )}
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-2">
            <div
              className={`h-full rounded-full transition-all ${cfg.barClass}`}
              style={{ width: `${cfg.pct}%` }}
            />
          </div>
          <p className="text-[11px] text-slate-500 line-clamp-2">{cfg.hint}</p>
          <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[#0B192C] group-hover:text-[#D4AF37] transition">
            {cfg.cta}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
